import React from 'react';
import { useTranslation } from 'react-i18next';

import type { DateRange, PeriodPreset } from '../../reports-helpers';
import { PillGroup, Pill, DateFields, DateField, DateArrow } from './dashboard.styles';

export type PresetId = 'month' | '7d' | '30d' | '90d';

export interface PeriodSelectorProps {
  preset: PeriodPreset;
  range: DateRange;
  onPreset: (preset: PresetId) => void;
  onCustom: (range: DateRange) => void;
}

export const PeriodSelector = ({ preset, range, onPreset, onCustom }: PeriodSelectorProps): React.ReactElement => {
  const { t } = useTranslation();
  // Literal-keyed labels so the i18n extractor harvests each (a dynamic t() would not).
  const presets: Array<{ id: PresetId; label: string }> = [
    { id: 'month', label: t('This month') },
    { id: '7d', label: t('7 days') },
    { id: '30d', label: t('30 days') },
    { id: '90d', label: t('90 days') },
  ];

  return (
    <>
      <PillGroup role="group" aria-label={t('Reporting period')}>
        {presets.map((p) => (
          <Pill key={p.id} type="button" $active={preset === p.id} onClick={() => onPreset(p.id)}>
            {p.label}
          </Pill>
        ))}
      </PillGroup>
      <DateFields>
        <DateField
          type="date"
          value={range.from}
          max={range.to}
          aria-label={t('From date')}
          onChange={(e) => {
            if (e.target.value) onCustom({ from: e.target.value, to: range.to });
          }}
        />
        <DateArrow aria-hidden="true">&rarr;</DateArrow>
        <DateField
          type="date"
          value={range.to}
          min={range.from}
          aria-label={t('To date')}
          onChange={(e) => {
            if (e.target.value) onCustom({ from: range.from, to: e.target.value });
          }}
        />
      </DateFields>
    </>
  );
};

export default PeriodSelector;
